import React, { createContext, useCallback, useRef, useContext } from 'react'

const Ctx = createContext(null)
export const useCsrf = () => useContext(Ctx)

export function CsrfProvider({ children }) {
  const token = useRef('')
  const exp   = useRef(0)
  const pending = useRef(null)

  const load = async () => {
    const res = await fetch('/api/csrf-token', { credentials: 'include' })
    if (!res.ok) throw new Error(`Unable to fetch CSRF token (${res.status})`)
    const { token: t } = await res.json().catch(() => ({}))
    if (typeof t !== 'string' || !t) throw new Error('CSRF token response was empty')
    token.current = t
    exp.current = Date.now() + 50 * 60 * 1000
    return t
  }

  const ensure = useCallback(async (force = false) => {
    if (!force && token.current && Date.now() < exp.current) return token.current
    if (force) { token.current = ''; exp.current = 0; pending.current = null }
    // Clearing `pending` in a finally is what keeps a single failed fetch from
    // being replayed to every later caller: a cached rejected promise made every
    // CSRF-protected POST for the rest of the session fail before it was sent.
    if (!pending.current) {
      pending.current = load().finally(() => { pending.current = null })
    }
    return pending.current
  }, [])

  const reset = useCallback(() => {
    token.current = ''
    exp.current = 0
    pending.current = null
  }, [])

  const contextValue = React.useMemo(() => ({ ensure, reset }), [ensure, reset])

  return <Ctx.Provider value={contextValue}>{children}</Ctx.Provider>
}
